/**
 * Launch Readiness API client.
 *
 * Thin fetch wrapper for the Builder Service readiness endpoint
 * (`GET /api/projects/:projectId/launch/v1/readiness`). Follows the
 * custom-domains client conventions: Load Balancer base URL from
 * `VITE_BUILDER_API_URL` + Firebase Auth bearer token.
 *
 * The endpoint responds `{ success: true, data }` with
 * `Cache-Control: no-store` — readiness is a live snapshot; this client
 * performs no caching of its own.
 */

import { getAuth } from 'firebase/auth';
import type { LaunchReadinessData } from '../types/launchReadiness.types';
import type { SafeRolloutRecord } from '../types/safeRollout.types';

export class LaunchReadinessApiError extends Error {
  code: string;
  status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = 'LaunchReadinessApiError';
    this.code = code;
    this.status = status;
  }
}

export interface LaunchReadinessRequestParams {
  funnelId?: string;
  versionId?: string;
  webPlacementId?: string;
  abTestId?: string;
}

function getApiBaseUrl(): string {
  const url = import.meta.env.VITE_BUILDER_API_URL;
  if (!url) {
    throw new LaunchReadinessApiError('VITE_BUILDER_API_URL is not configured', 'CONFIG_MISSING', 0);
  }
  return url;
}

async function getAuthToken(): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new LaunchReadinessApiError('Not authenticated', 'UNAUTHORIZED', 401);
  }
  return user.getIdToken();
}

export const launchReadinessClient = {
  async getReadiness(
    projectId: string,
    params: LaunchReadinessRequestParams = {},
  ): Promise<LaunchReadinessData> {
    const token = await getAuthToken();
    const baseUrl = getApiBaseUrl();

    const query = new URLSearchParams();
    if (params.funnelId) query.set('funnelId', params.funnelId);
    if (params.versionId) query.set('versionId', params.versionId);
    if (params.webPlacementId) query.set('webPlacementId', params.webPlacementId);
    if (params.abTestId) query.set('abTestId', params.abTestId);
    const queryString = query.toString();

    const response = await fetch(
      `${baseUrl}/api/projects/${encodeURIComponent(projectId)}/launch/v1/readiness${queryString ? `?${queryString}` : ''}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
    );

    const body = await response.json().catch(() => ({}));
    if (!response.ok || body?.success === false) {
      throw new LaunchReadinessApiError(
        body.error || body.message || `API error: ${response.status}`,
        body.code || 'UNKNOWN_ERROR',
        response.status,
      );
    }
    return body.data as LaunchReadinessData;
  },

  // ── Safe rollouts (J2-I2) ─────────────────────────────────────────────────
  // SafeRolloutController envelope: `{ apiVersion, asOf, data, warnings }` on
  // success; RFC-7807 problem+json (`{ title, detail, code, status }`) on
  // error — unwrapped here so callers only ever see SafeRolloutRecord.

  /** Newest-first list of the project's safe rollouts. */
  async listSafeRollouts(projectId: string): Promise<SafeRolloutRecord[]> {
    const body = await safeRolloutRequest(projectId, '', 'GET');
    return (body?.safeRollouts ?? []) as SafeRolloutRecord[];
  },

  /** Runs one monitoring look now; returns the updated record. */
  async evaluateSafeRollout(projectId: string, safeRolloutId: string): Promise<SafeRolloutRecord> {
    const body = await safeRolloutRequest(projectId, `/${encodeURIComponent(safeRolloutId)}/evaluate`, 'POST');
    return body?.safeRollout as SafeRolloutRecord;
  },

  /** One-click rollback to the previous version; returns the closed record. */
  async rollbackSafeRollout(projectId: string, safeRolloutId: string): Promise<SafeRolloutRecord> {
    const body = await safeRolloutRequest(projectId, `/${encodeURIComponent(safeRolloutId)}/rollback`, 'POST');
    return body?.safeRollout as SafeRolloutRecord;
  },
};

/** Shared fetch + envelope unwrap for the safe-rollout endpoints. */
async function safeRolloutRequest(
  projectId: string,
  suffix: string,
  method: 'GET' | 'POST',
): Promise<Record<string, any>> {
  const token = await getAuthToken();
  const baseUrl = getApiBaseUrl();

  const response = await fetch(
    `${baseUrl}/api/projects/${encodeURIComponent(projectId)}/launch/v1/safe-rollouts${suffix}`,
    {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    // problem+json: `detail` carries the human-readable message.
    throw new LaunchReadinessApiError(
      body.detail || body.title || body.error || `API error: ${response.status}`,
      body.code || 'UNKNOWN_ERROR',
      response.status,
    );
  }
  return (body?.data ?? {}) as Record<string, any>;
}

export default launchReadinessClient;
